import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Btn, Table, Badge } from '../../components/UI';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function Widget() {
  const [widget, setWidget] = useState(null);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [ws, ls] = await Promise.all([api.get('/api/widget/settings'), api.get('/api/widget/leads')]);
    if (ws.data.success && ws.data.widget) setWidget(ws.data.widget);
    if (ls.data.success) setLeads(ls.data.leads);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!widget) return; setSaving(true);
    const { data } = await api.put('/api/widget/settings', widget);
    if (data.success) toast.success('Widget settings saved!');
    else toast.error(data.message);
    setSaving(false);
  };

  const regenKey = async () => {
    if (!window.confirm('Regenerate API key? Old install code will stop working!')) return;
    const { data } = await api.post('/api/widget/regenerate-key', {});
    if (data.success) { setWidget(w => ({ ...w, api_key: data.api_key })); toast.success('API key regenerated!'); }
  };

  const installCode = widget ? `<script src="https://api.msreach.msdiscoveries.com/api/widget/script/${widget.api_key}"></script>` : '';

  return (
    <div>
      <PageHeader title="Lead Capture Widget" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <Card title="⚙️ Widget Settings">
          {widget ? (
            <>
              {[['Widget Title','title','Contact Us on WhatsApp'],['Button Text','button_text','Send Message']].map(([label,key,ph]) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 500 }}>{label}</label>
                  <input value={widget[key] || ''} onChange={e => setWidget({ ...widget, [key]: e.target.value })} placeholder={ph} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text)', fontSize: 14 }} />
                </div>
              ))}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 500 }}>Button Color</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="color" value={widget.button_color || '#00E74C'} onChange={e => setWidget({ ...widget, button_color: e.target.value })} style={{ width: 50, height: 40, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', background: 'none' }} />
                  <input value={widget.button_color || '#00E74C'} onChange={e => setWidget({ ...widget, button_color: e.target.value })} style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text)', fontSize: 14 }} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 500 }}>Position</label>
                <select value={widget.position || 'bottom_right'} onChange={e => setWidget({ ...widget, position: e.target.value })} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text)', fontSize: 14 }}>
                  <option value="bottom_right">Bottom Right</option>
                  <option value="bottom_left">Bottom Left</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <input type="checkbox" id="wActive" checked={widget.is_active} onChange={e => setWidget({ ...widget, is_active: e.target.checked })} />
                <label htmlFor="wActive" style={{ fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>Widget Active</label>
              </div>
              <Btn onClick={save} disabled={saving} full>{saving ? 'Saving...' : '💾 Save Settings'}</Btn>
            </>
          ) : <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Loading...</div>}
        </Card>

        <div>
          <Card title="🔑 Your API Key" style={{ marginBottom: 16 }}>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, fontFamily: 'monospace', fontSize: 13, color: 'var(--green)', wordBreak: 'break-all', marginBottom: 10 }}>{widget?.api_key || 'Loading...'}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="outline" onClick={() => { navigator.clipboard.writeText(widget?.api_key || ''); toast.success('Copied!'); }} full>📋 Copy Key</Btn>
              <Btn variant="danger" onClick={regenKey} full>🔄 Regenerate</Btn>
            </div>
          </Card>
          <Card title="📋 Install Code">
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>Paste this before the &lt;/body&gt; tag on your website:</p>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, fontFamily: 'monospace', fontSize: 12, color: '#8ab4ff', wordBreak: 'break-all', lineHeight: 1.6, marginBottom: 10 }}>{installCode}</div>
            <Btn variant="outline" onClick={() => { navigator.clipboard.writeText(installCode); toast.success('Install code copied!'); }} full>📋 Copy Install Code</Btn>
          </Card>
        </div>
      </div>

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>📋 Widget Leads <span style={{ color: 'var(--green)', fontWeight: 400 }}>({leads.length} total)</span></div>
        <Table emptyMsg="No leads yet. Install the widget on your website!" loading={false}
          columns={[
            { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
            { key: 'whatsapp', label: 'WhatsApp', render: v => <span style={{ color: 'var(--green)' }}>{v || '—'}</span> },
            { key: 'email', label: 'Email', render: v => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v || '—'}</span> },
            { key: 'message', label: 'Message', render: v => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{(v || '—').substring(0, 40)}</span> },
            { key: 'status', label: 'Status', render: v => <Badge>{v}</Badge> },
            { key: 'created_at', label: 'Date', render: v => new Date(v).toLocaleDateString('en-IN') },
          ]}
          data={leads}
        />
      </div>
    </div>
  );
}